/**
 * SofiaRobot v2 — High-tech AI assistant avatar.
 *
 * Concept: an evolution of the Commandix cable isotype into a sci-fi-grade
 * AI companion. PCB-style traces, orbital energy core, horizontal visor,
 * data pulses traveling along cables, neon glow.
 *
 * Props:
 *   size       : px size (default 80)
 *   state      : "idle" | "wave" | "typing"
 *   className  : extra wrapper classes
 */
import React from "react";

export default function SofiaRobot({ size = 80, state = "idle", className = "" }) {
  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      data-testid="sofia-robot"
      data-state={state}
    >
      {/* Outer aura — soft radial glow */}
      <span
        className="absolute inset-[-18%] rounded-full pointer-events-none animate-sofia-aura"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(255,138,40,0.55) 0%, rgba(255,61,0,0.18) 40%, rgba(255,61,0,0) 72%)",
          filter: "blur(8px)",
        }}
      />

      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={`relative ${state === "wave" ? "sofia-bot-bounce" : "sofia-bot-idle"}`}
        style={{ overflow: "visible" }}
      >
        <defs>
          {/* Capsule body — metallic orange with depth */}
          <linearGradient id="cxBody2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFD8A8" />
            <stop offset="35%" stopColor="#FF8A28" />
            <stop offset="100%" stopColor="#7A1F00" />
          </linearGradient>
          {/* Highlight on top of head */}
          <linearGradient id="cxBodyHi" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.65)" />
            <stop offset="60%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          {/* Cable / circuit trace */}
          <linearGradient id="cxTrace" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFD8A8" />
            <stop offset="100%" stopColor="#FF3D00" />
          </linearGradient>
          {/* Energy core — radial */}
          <radialGradient id="cxCore2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="35%" stopColor="#FFE3C7" />
            <stop offset="70%" stopColor="#FF7A00" />
            <stop offset="100%" stopColor="#7A1F00" />
          </radialGradient>
          {/* Visor */}
          <linearGradient id="cxVisor" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFC78A" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#FFC78A" stopOpacity="0.2" />
          </linearGradient>
          {/* Neon glow filter */}
          <filter id="cxNeon" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.8" result="b1" />
            <feMerge>
              <feMergeNode in="b1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Strong glow for the core */}
          <filter id="cxNeonStrong" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ===== PCB-like circuit traces (back layer) ===== */}
        <g opacity="0.55" filter="url(#cxNeon)">
          {/* Left circuit network */}
          <path d="M 18 70 L 18 60 L 26 60 L 26 52" fill="none" stroke="url(#cxTrace)" strokeWidth="0.9" />
          <path d="M 12 78 L 22 78 L 22 70" fill="none" stroke="url(#cxTrace)" strokeWidth="0.7" />
          <circle cx="18" cy="70" r="1" fill="#FFD8A8" />
          <circle cx="26" cy="60" r="1" fill="#FFD8A8" />
          <circle cx="22" cy="78" r="1" fill="#FFD8A8" />
          {/* Right circuit network */}
          <path d="M 82 70 L 82 60 L 74 60 L 74 52" fill="none" stroke="url(#cxTrace)" strokeWidth="0.9" />
          <path d="M 88 78 L 78 78 L 78 70" fill="none" stroke="url(#cxTrace)" strokeWidth="0.7" />
          <circle cx="82" cy="70" r="1" fill="#FFD8A8" />
          <circle cx="74" cy="60" r="1" fill="#FFD8A8" />
          <circle cx="78" cy="78" r="1" fill="#FFD8A8" />
        </g>

        {/* ===== Energy traveling along the cables ===== */}
        {/* Left main cable — body curve */}
        <path
          id="cxLeftCable"
          d="M 50 22 C 30 32, 22 50, 26 74"
          fill="none"
          stroke="url(#cxTrace)"
          strokeWidth="3"
          strokeLinecap="round"
          filter="url(#cxNeon)"
        />
        {/* Right main cable */}
        <path
          id="cxRightCable"
          d="M 50 22 C 70 32, 78 50, 74 74"
          fill="none"
          stroke="url(#cxTrace)"
          strokeWidth="3"
          strokeLinecap="round"
          filter="url(#cxNeon)"
        />
        {/* Data pulses traveling down the cables */}
        <circle r="1.6" fill="#FFFFFF" filter="url(#cxNeonStrong)">
          <animateMotion dur="2.2s" repeatCount="indefinite" path="M 50 22 C 30 32, 22 50, 26 74" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle r="1.6" fill="#FFFFFF" filter="url(#cxNeonStrong)">
          <animateMotion dur="2.2s" begin="1.1s" repeatCount="indefinite" path="M 50 22 C 70 32, 78 50, 74 74" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="2.2s" begin="1.1s" repeatCount="indefinite" />
        </circle>

        {/* ===== Connector nodes at the ends (logo signature) ===== */}
        <circle cx="26" cy="74" r="3" fill="#FF7A00" stroke="#FFD8A8" strokeWidth="0.6" filter="url(#cxNeon)">
          <animate attributeName="r" values="2.6;3.4;2.6" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="74" cy="74" r="3" fill="#FF7A00" stroke="#FFD8A8" strokeWidth="0.6" filter="url(#cxNeon)">
          <animate attributeName="r" values="2.6;3.4;2.6" dur="2.2s" begin="1.1s" repeatCount="indefinite" />
        </circle>

        {/* ===== Static left arm cable ===== */}
        <path
          d="M 30 56 C 20 54, 14 48, 16 40"
          fill="none"
          stroke="url(#cxTrace)"
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.85"
          filter="url(#cxNeon)"
        />
        <circle cx="16" cy="40" r="2.4" fill="#FFB266" stroke="#FF7A00" strokeWidth="0.6" filter="url(#cxNeon)" />

        {/* ===== Waving arm cable ===== */}
        <g
          className={state === "wave" ? "sofia-bot-wave" : "sofia-bot-arm-idle"}
          style={{ transformOrigin: "70px 56px" }}
        >
          <path
            d="M 70 56 C 80 54, 88 46, 86 36"
            fill="none"
            stroke="url(#cxTrace)"
            strokeWidth="2.8"
            strokeLinecap="round"
            filter="url(#cxNeon)"
          />
          {/* "Hand" node */}
          <circle cx="86" cy="36" r="3" fill="#FFD8A8" stroke="#FF7A00" strokeWidth="0.8" filter="url(#cxNeonStrong)">
            <animate attributeName="r" values="2.6;3.2;2.6" dur="1.6s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* ===== Head capsule ===== */}
        <g className={state === "typing" ? "sofia-bot-head-tilt" : ""} style={{ transformOrigin: "50px 50px" }}>
          {/* Antenna */}
          <line x1="50" y1="22" x2="50" y2="14" stroke="url(#cxTrace)" strokeWidth="1.6" strokeLinecap="round" filter="url(#cxNeon)" />
          <circle cx="50" cy="13" r="2.2" fill="#FFD8A8" filter="url(#cxNeonStrong)">
            <animate attributeName="r" values="1.8;2.8;1.8" dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="fill-opacity" values="0.6;1;0.6" dur="1.4s" repeatCount="indefinite" />
          </circle>

          {/* Head shadow base */}
          <ellipse cx="50" cy="42" rx="22.5" ry="20" fill="#1a0d05" />
          {/* Head capsule */}
          <ellipse
            cx="50"
            cy="40"
            rx="22"
            ry="20"
            fill="url(#cxBody2)"
            stroke="#FF7A00"
            strokeWidth="0.6"
          />
          {/* Specular highlight */}
          <ellipse cx="50" cy="32" rx="14" ry="6" fill="url(#cxBodyHi)" opacity="0.55" />

          {/* PCB head traces (inner detail) */}
          <g opacity="0.45" stroke="#FFD8A8" strokeWidth="0.4" fill="none">
            <path d="M 34 38 L 38 38" />
            <path d="M 62 38 L 66 38" />
            <path d="M 34 44 L 38 44" />
            <path d="M 62 44 L 66 44" />
            <circle cx="38" cy="38" r="0.6" fill="#FFD8A8" />
            <circle cx="62" cy="38" r="0.6" fill="#FFD8A8" />
            <circle cx="38" cy="44" r="0.6" fill="#FFD8A8" />
            <circle cx="62" cy="44" r="0.6" fill="#FFD8A8" />
          </g>

          {/* Visor — dark inset */}
          <rect x="34" y="36" width="32" height="9" rx="4.5" fill="#0a0500" opacity="0.92" />
          <rect x="34" y="36" width="32" height="9" rx="4.5" fill="none" stroke="#FF7A00" strokeWidth="0.4" />
          {/* Visor shine — moving sweep (data scan) */}
          <rect x="34" y="36" width="32" height="9" rx="4.5" fill="url(#cxVisor)" opacity="0.18">
            <animate attributeName="x" values="-32;66" dur="3.2s" repeatCount="indefinite" />
          </rect>
          {/* Two AI "eye" lights */}
          <g className="sofia-bot-eyes">
            <circle cx="42" cy="40.5" r="1.5" fill="#FFFFFF" filter="url(#cxNeonStrong)">
              <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx="58" cy="40.5" r="1.5" fill="#FFFFFF" filter="url(#cxNeonStrong)">
              <animate attributeName="opacity" values="1;0.55;1" dur="2.4s" begin="0.4s" repeatCount="indefinite" />
            </circle>
          </g>

          {/* Subtle smile guide curve under visor */}
          <path
            d="M 44 50.5 Q 50 53, 56 50.5"
            stroke="#FF7A00"
            strokeWidth="0.8"
            strokeLinecap="round"
            fill="none"
            opacity="0.5"
            filter="url(#cxNeon)"
          />
        </g>

        {/* ===== Energy core (chest) — orbital ring + pulsing core ===== */}
        <g>
          {/* Orbital ring (rotates) */}
          <g style={{ transformOrigin: "50px 70px" }} className="sofia-bot-orbit">
            <circle cx="50" cy="70" r="9" fill="none" stroke="#FF7A00" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.7" />
            <circle cx="59" cy="70" r="1" fill="#FFD8A8" filter="url(#cxNeonStrong)" />
          </g>
          {/* Outer halo ring */}
          <circle cx="50" cy="70" r="7" fill="none" stroke="#FF7A00" strokeWidth="0.5" opacity="0.45">
            <animate attributeName="r" values="6;8.5;6" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2s" repeatCount="indefinite" />
          </circle>
          {/* Core ball */}
          <circle
            cx="50"
            cy="70"
            r="5"
            fill="url(#cxCore2)"
            filter="url(#cxNeonStrong)"
            className={state === "typing" ? "sofia-bot-core-pulse" : "sofia-bot-core-breathe"}
            style={{ transformOrigin: "50px 70px" }}
          />
          {/* Core hot center */}
          <circle cx="50" cy="70" r="1.8" fill="#FFFFFF" opacity="0.95" />
        </g>

        {/* ===== Bottom signal dots ===== */}
        <g>
          <circle cx="40" cy="88" r="1.2" fill="#FF7A00">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="50" cy="90" r="1.2" fill="#FF7A00">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.5s" begin="0.25s" repeatCount="indefinite" />
          </circle>
          <circle cx="60" cy="88" r="1.2" fill="#FF7A00">
            <animate attributeName="opacity" values="0.2;1;0.2" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>
    </div>
  );
}
